// UTILS
import { ref } from "vue"
import { defineStore, storeToRefs } from "pinia"
import { MixpanelInstance } from "@/plugins/mixpanel/mixpanel"
import Bugsnag from "@bugsnag/js"

// TYPES
import type { Answers, Answer } from "@/features/drive/store/drive.types"

// STORES
import {
  initAnswers,
  initExtra,
  factoryPatch,
  factoryReset,
} from "@/features/drive/store/drive.data"
import { useI18nStore } from "@/stores/i18n"
import { useUserStore } from "@/stores/user"
import { useQuestionsStore } from "@/features/drive/store/questions.store"

// API
import {
  getAssessment,
  patchAssessment,
  postAssessment,
  deleteAssessment,
} from "@/api/viktor/assessment/assessment"

// HELPERS
import { getVersionFromLang } from "@/features/drive/helpers/getVersionFromLang"
import { getDeviceType } from "@/helpers/getDeviceType"

export const useAnswersStore = defineStore("driveAnswersStore", () => {
  const { goNext, setCurrentIndex } = useQuestionsStore()
  const i18nStore = useI18nStore()
  const { updateLocale, getLocaleIdFromIsoCode } = i18nStore
  const { nextAssessment } = storeToRefs(useUserStore())
  const { currentLocale } = storeToRefs(useI18nStore())
  const { isFromInt } = storeToRefs(useUserStore())

  // ref
  const answers = ref<Answers>(initAnswers)

  const userStore = useUserStore()

  // GETTERS

  /**
   * Fetch the drive assessment from the API
   * @returns
   */
  async function fetchDrive() {
    const driveAssessment = userStore.assessmentSummary.assessments.drive
    const canRestart =
      driveAssessment?.can_restart ||
      driveAssessment?.retake_test ||
      (nextAssessment.value?.next_assessment.assessment === "drive" &&
        nextAssessment.value?.next_assessment.retake)

    if (
      driveAssessment?.status === "finish" &&
      !driveAssessment?.can_restart &&
      !driveAssessment?.retake_test &&
      !driveAssessment.not_passed
    ) {
      // si le test est terminé, on sort de l'assessment
      if (isFromInt.value) answers.value.status = "thank-you"
      else window.location.reload()
    } else if (driveAssessment?.status === null) {
      // si le test n'a jamais été commencé on l'initalise
      await initDrive(false, null)
    } else if (driveAssessment?.status == "finish" && canRestart) {
      // si le status est finish et que le test date de plus d'un an
      // delete + init new test
      await deleteAssessment("drive")
      await initDrive(false, null)
    } else if (
      driveAssessment?.status === "todo" ||
      driveAssessment?.status === "inprogress" ||
      !!driveAssessment?.not_passed
    ) {
      const driveData = await syncAssessment()

      // No active assessment found (drive?is-active=1 returned {}): initialize it
      if (!driveData?.status) {
        await initDrive(false, null)
        return answers.value
      }

      // autrement c'est que le test est en cours (inprogress)
      // donc on récupère les données du test et on popule les stores
      if (Object.keys(answers.value?.data || {}).length === 0) {
        setCurrentIndex(0)
      } else {
        setCurrentIndex(answers.value.data?.items?.length)
      }

      answers.value.data.extra = driveData.data?.extra ?? initAnswers.data.extra
      answers.value.data.items = driveData.data?.items ?? []

      // sometimes version was not set in the assessment
      let versionFull
      if (answers.value?.version === null) {
        versionFull = getVersionFromLang(currentLocale.value.iso_code)
        answers.value.version = versionFull
      } else {
        versionFull = answers.value.version
      }

      const lastUnderscoreIndex = versionFull.lastIndexOf("_")
      const langCode = versionFull.substring(0, lastUnderscoreIndex)
      userStore.audio = answers.value.data?.extra?.general_audio ? true : false

      if (
        langCode !== currentLocale.value.iso_code &&
        answers.value?.status === "inprogress"
      ) {
        const locale_id = getLocaleIdFromIsoCode(langCode)
        await updateLocale(locale_id)
        // @TODO: set trad key from luciano
        // toast.info("Language change")
      }
    }

    return answers.value
  }

  /**
   * Get the elapsed time from the answers
   * @returns
   */
  function getElapsedTime() {
    let time = 0
    answers.value.data.items.map((item) => (time += item.time_initial))
    return Math.round(time)
  }

  // ACTIONS

  /**
   * Initialize the drive assessment
   * @param restarting
   * @param lang
   * @returns
   */
  async function initDrive(restarting?: boolean, lang?: number | null) {
    const payload: Answers = {
      version: getVersionFromLang(currentLocale.value.iso_code),
      locale_id: lang ? lang : currentLocale.value.id,
      status: "todo",
      data: { items: [], extra: { ...initExtra, isRestarting: restarting } },
    }
    const data = await postAssessment("drive", payload)
    answers.value = payload
    setCurrentIndex(0)

    return data
  }

  /**
   * Sync the answers with the backend
   */
  async function syncAssessment() {
    const assessment = await getAssessment("drive")
    const currentAnswers = answers.value

    const newLength = assessment?.data?.items?.length ?? 0
    const currentLength = answers.value?.data?.items?.length ?? 0

    answers.value = assessment

    // We don't want to override the answers if the new length is less than the current length
    if (newLength < currentLength) {
      answers.value.data.items = currentAnswers.data.items
    }

    return assessment
  }

  /**
   * Submit an answer to the current question
   * @param answer
   * @param time
   */
  async function submitAnswer(answer: string, time: number) {
    if (
      !answers.value.data ||
      (Array.isArray(answers.value.data) && answers.value.data.length === 0)
    ) {
      answers.value.data = { items: [], extra: initExtra }
    }

    // split answer string by _ to get the question id
    const questionId = answer.split("_PROPOSAL_")[0]

    // check if the answer has been already answered from answer param starts with
    const alreadyAnswered = answers.value.data.items.find((a) =>
      a.answer.includes(questionId)
    )

    if (!alreadyAnswered) {
      const item: Answer = {
        answer: answer,
        audio: userStore.audio,
        device_type: getDeviceType(),
        screen_width: screen.width,
        time_initial: time,
      }
      answers.value.data.items.push(item)

      answers.value.data.extra.general_audio = userStore.audio

      const payload: Answers = {
        version: getVersionFromLang(currentLocale.value.iso_code),
        locale_id: currentLocale.value.id,
        data: answers.value.data,
      }
      await patchAssessment("drive", payload)
    }
    await goNext()
  }

  /**
   * Submit the final results of the drive assessment
   * @param lang
   */
  async function submitFinalResults(lang?: number | null) {
    try {
      await syncAssessment()

      answers.value.data.extra.elapsed_time = getElapsedTime()
      answers.value.data.extra.finished = true
      // TODO: remove when backend no longer requires these fields
      answers.value.data.extra.native_language ??= true
      answers.value.data.extra.wholesome_conditions ??= true

      // if array length more than 90, we remove the last items [QA-7694]
      answers.value.data.items.length > 90 &&
        answers.value.data.items.splice(90)

      const payload: Answers = {
        version: getVersionFromLang(currentLocale.value.iso_code),
        locale_id: lang || currentLocale.value.id,
        data: answers.value.data,
      }

      // ultime patch to finish the test
      await patchAssessment("drive", payload)

      MixpanelInstance.track("drive_finish", {
        payload: payload,
      })
    } catch (error) {
      Bugsnag.notify(error, (event) => {
        event.context = "DriveAnswersStore.submitFinalResults"
        event.severity = "error"
        event.unhandled = false
        event.addMetadata("request", {
          lang: lang,
          payload: {
            version: getVersionFromLang(currentLocale.value.iso_code),
            locale_id: lang || currentLocale.value.id,
            data: answers.value.data,
          },
        })
      })
    }
  }

  //-- FOR AF QUICK TESTS

  /**
   * Patch the drive assessment with factory data
   * @param isRestarting
   */
  async function patchFactoryTest(isRestarting: boolean) {
    const payload = factoryPatch(isRestarting ? isRestarting : undefined) // Leave undefined as default
    await patchAssessment("drive", payload)
    window.location.reload()
  }

  /**
   * Reset the user data for the drive assessment
   */
  async function resetUserDataLight() {
    await patchAssessment("drive", factoryReset)
    window.location.reload()
  }

  return {
    answers,
    fetchDrive,
    submitAnswer,
    submitFinalResults,
    patchFactoryTest,
    resetUserDataLight,
  }
})
